import React, { useState, useEffect } from 'react';
import { logPwaInstallPrompted, logPwaInstallOutcome } from '../../services/analytics';

const InstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Check if running as standalone PWA
        const standalone = window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;
        setIsStandalone(standalone);

        // Detect iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(iOS);

        // Listen for beforeinstallprompt event (Android/Chrome)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowInstallPrompt(true);
            logPwaInstallPrompted();
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Show iOS prompt if on iOS and not installed
        if (iOS && !standalone) {
            // Show iOS prompt after a delay
            const timer = setTimeout(() => {
                setShowInstallPrompt(true);
            }, 3000);
            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
                clearTimeout(timer);
            };
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response: ${outcome}`);
        logPwaInstallOutcome(outcome);

        // Clear the deferred prompt
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
    };

    const handleDismiss = () => {
        setShowInstallPrompt(false);
        // Don't show again for 7 days
        localStorage.setItem('installPromptDismissed', Date.now().toString());
    };

    // Don't show if already dismissed recently
    const dismissedTime = localStorage.getItem('installPromptDismissed');
    if (dismissedTime) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
            return null;
        }
    }

    // Don't show if already installed
    if (isStandalone) return null;

    // Don't show if dismissed
    if (!showInstallPrompt) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 mx-auto max-w-md md:mx-0">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border-2 border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                            <svg className="w-7 h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base md:text-lg text-gray-900 dark:text-white truncate">
                                Install Calendar Manager
                            </h3>
                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                                Quick access, works offline
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1 -mt-1 -mr-1 touch-manipulation"
                        aria-label="Dismiss"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {isIOS ? (
                    <div className="space-y-3">
                        <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 font-medium">
                            Install this app on your iPhone:
                        </p>
                        <ol className="text-sm md:text-base text-gray-600 dark:text-gray-400 space-y-2 pl-4">
                            <li className="flex items-start gap-2">
                                <span className="font-semibold flex-shrink-0">1.</span>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span>Tap the Share button</span>
                                    <svg className="w-5 h-5 flex-shrink-0 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                                    </svg>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="font-semibold flex-shrink-0">2.</span>
                                <span>Tap "Add to Home Screen"</span>
                            </li>
                        </ol>
                        <button
                            onClick={handleDismiss}
                            className="w-full mt-3 px-4 py-3 md:py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 active:bg-gray-400 dark:hover:bg-gray-600 dark:active:bg-gray-500 transition-colors font-medium text-sm md:text-base touch-manipulation"
                        >
                            Got it
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-2 mt-3">
                        <button
                            onClick={handleInstallClick}
                            className="flex-1 px-4 py-3 md:py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 active:from-blue-700 active:to-blue-800 transition-all font-medium text-sm md:text-base shadow-md hover:shadow-lg touch-manipulation"
                        >
                            Install
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="px-4 py-3 md:py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 active:bg-gray-400 dark:hover:bg-gray-600 dark:active:bg-gray-500 transition-colors font-medium text-sm md:text-base touch-manipulation"
                        >
                            Not now
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstallPrompt;
